import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import LocationCoverage from "@/components/LocationCoverage";
import { toAbsoluteUrl } from "@/lib/seo";

// Lazy load below-the-fold components
const BentoGrid = lazy(() => import("@/components/BentoGrid"));
const RevenueCalculator = lazy(() => import("@/components/RevenueCalculator"));
const SocialProof = lazy(() => import("@/components/SocialProof"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const PricingTeaser = lazy(() => import("@/components/PricingTeaser"));
const FAQ = lazy(() => import("@/components/FAQ"));

type DeferredSectionProps = {
  children: ReactNode;
  minHeight?: number;
  rootMargin?: string;
  forceRender?: boolean;
};

const DeferredSection = ({ children, minHeight = 400, rootMargin = "600px 0px", forceRender = false }: DeferredSectionProps) => {
  const markerRef = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(forceRender);

  useEffect(() => {
    if (forceRender && !shouldRender) {
      setShouldRender(true);
    }
  }, [forceRender, shouldRender]);

  useEffect(() => {
    if (shouldRender) return;

    if (!("IntersectionObserver" in window)) {
      setShouldRender(true);
      return;
    }

    const marker = markerRef.current;
    if (!marker) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(marker);
    return () => observer.disconnect();
  }, [rootMargin, shouldRender]);

  return (
    <div ref={markerRef} className="cv-auto">
      {shouldRender ? children : <div style={{ minHeight }} aria-hidden="true" />}
    </div>
  );
};

const homeSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ParvatiiStays",
    url: toAbsoluteUrl("/"),
  },
  {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "ParvatiiStays",
    url: toAbsoluteUrl("/"),
    areaServed: ["Northern Beaches", "North Shore", "Lower North Shore", "Eastern Suburbs", "Sydney CBD", "Inner Sydney"],
    serviceType: "Short-term rental property management",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is your management fee?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Our core management fee is 20% inc. GST of net booking revenue. There are no hidden fees — no booking fees, no maintenance mark-ups. You keep 80% of every dollar your property earns.",
        },
      },
      {
        "@type": "Question",
        name: "Is there a lock-in contract?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No lock-in contracts. We work on a rolling basis with 30 days notice. We earn your business every month — if we're not performing, you're free to leave.",
        },
      },
      {
        "@type": "Question",
        name: "What is STRA and do I need to register?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "STRA (Short-Term Rental Accommodation) is NSW's regulatory framework for Airbnb-style rentals. Most Sydney properties require registration with NSW Fair Trading and compliance with council rules. We handle the entire registration and ongoing compliance process on your behalf.",
        },
      },
      {
        "@type": "Question",
        name: "Which platforms will my property be listed on?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We list on Airbnb, Booking.com, and Stayz simultaneously from day one. All channels are managed from a single dashboard so pricing, availability, and guest communication stay perfectly synchronised.",
        },
      },
      {
        "@type": "Question",
        name: "How quickly can my property go live?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most properties go live within 2–3 weeks of signing. That includes a property audit, professional photography, listing creation, and platform setup. For properties that need styling or minor works, we'll provide a timeline after the initial assessment.",
        },
      },
      {
        "@type": "Question",
        name: "What happens if a guest damages the property?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We coordinate all damage claims through Airbnb's AirCover protection and our comprehensive short-term rental insurance policy. Our team conducts a quality inspection after every guest checkout so damage is caught immediately and documented.",
        },
      },
      {
        "@type": "Question",
        name: "Can I still use my property myself?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Absolutely. Owner stays are always available. Simply block the dates you want through your owner dashboard and we'll make sure those nights are removed from guest availability.",
        },
      },
      {
        "@type": "Question",
        name: "Do you service all of Sydney?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We specialise in Northern Beaches, North Shore, Lower North Shore, Eastern Suburbs, and Sydney CBD. These markets are where our operational team, cleaning crews, and local knowledge are strongest. Reach out if you're not sure — we assess on a case-by-case basis.",
        },
      },
    ],
  },
];

const Index = () => {
  const { hash } = useLocation();
  const activeHash = decodeURIComponent(hash.replace(/^#/, ""));

  return (
    <main className="min-h-screen bg-background">
      <Seo
        title="Sydney Short-Term Rental Management"
        description="Boutique Airbnb and short-term rental management across Northern Beaches, North Shore, Lower North Shore, Eastern Suburbs, Sydney CBD, and Inner Sydney."
        path="/"
        schema={homeSchema}
      />
      <Navbar />
      <HeroSection />
      <div className="cv-auto">
        <LocationCoverage />
      </div>
      <DeferredSection minHeight={460} forceRender={activeHash === "services"}>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <BentoGrid />
        </Suspense>
      </DeferredSection>
      <DeferredSection minHeight={520} forceRender={activeHash === "calculator"}>
        <Suspense fallback={<div className="min-h-[420px]" />}>
          <RevenueCalculator />
        </Suspense>
      </DeferredSection>
      <DeferredSection minHeight={560} forceRender={activeHash === "testimonials"}>
        <Suspense fallback={<div className="min-h-[440px]" />}>
          <SocialProof />
        </Suspense>
      </DeferredSection>
      <DeferredSection minHeight={520} forceRender={activeHash === "how-it-works"}>
        <Suspense fallback={<div className="min-h-[420px]" />}>
          <HowItWorks />
        </Suspense>
      </DeferredSection>
      <DeferredSection minHeight={480} forceRender={activeHash === "pricing"}>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <PricingTeaser />
        </Suspense>
      </DeferredSection>
      <DeferredSection minHeight={600} forceRender={activeHash === "faq"}>
        <Suspense fallback={<div className="min-h-[500px]" />}>
          <FAQ />
        </Suspense>
      </DeferredSection>
      <Footer />
    </main>
  );
};

export default Index;
